import React from 'react';
import { Table } from 'reactstrap';
import Select from 'react-select';
import Collapsible from 'react-collapsible';
import EditableField from './EditableField';
import HelpPopover from './HelpPopover';
import LANGUAGES from '../constants/languages';
import { matchesSimpleString, matchesDateString } from '../helpers/Validations';
import { SIMPLE_STRING, DATE_STRING } from '../constants/ValidationWarnings';

const valuesFromIds = (opts, ids) => { return (!!ids && !!opts) ? opts.filter(o => ids.includes(o.value)) : null;}

export default class ProjectMetadataExtended extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      languages: this.getLanguages(),
    };
  }

  getLanguages() {
    return this.props.languages || [];
  }

  handleSelectChange(value_object, name) {
    const values = value_object.map((val) => val.value);
    this.setState({ [name]: values });
    const selected = values ? values : [];
    this.props.projectUpdate(name, selected);
  }

  getTitles() {
    return [
      {
        title: 'Subtitle',
        attr: 'subtitle',
        text: this.props.subtitle,
        editTitle: 'Enter a subtitle',
        description: <span>
          An explanatory or alternate title,
          e.g., <em>or, the Modern Prometheus</em> in <em>Frankenstein; or, the Modern Prometheus</em>.
        </span>,
      },
      {
        title: 'Short Title',
        attr: 'short_title',
        text: this.props.short_title,
        editTitle: 'Enter a short title',
        description: <span>
          A shortened form of the title,
          e.g., <em>Huck Finn</em> for <em>The Adventures of Huckleberry Finn</em>.
        </span>,
      },
      {
        title: 'Collection Title',
        attr: 'collection_title',
        text: this.props.collection_title,
        editTitle: 'Enter a collection title',
        description: <span>
          The title of a series or collection to which the title belongs,
          e.g., <em>The Baby-Sitters Club</em> for <em>Claudia and the Phantom
            Phone Calls</em>.
        </span>,
      },
      {
        title: 'Extended Title',
        attr: 'extended_title',
        text: this.props.extended_title,
        editTitle: 'Enter an extended title',
        description: <span>
          A form of the title that includes all of its parts,
          e.g., <em>Mastering the Art of French Cooking, Volume I: 40th
            Anniversary Edition</em>.
        </span>,
      },
      {
        title: 'Edition Title',
        attr: 'edition_title',
        text: this.props.edition_title,
        editTitle: 'Enter an edition title',
        description: <span>
          The edition number,
          e.g., <em>17th Edition</em> in <em>The Chicago Manual of Style, 17th
            Edition</em>.
        </span>,
      },
    ];
  }

  getOtherFields() {
    return [
      {
        title: 'Publication Date',
        attr: 'pubdate',
        text: this.props.pubdate,
        validation: matchesDateString,
        warning: DATE_STRING,
        editTitle: 'Enter a publication date',
      },
      {
        title: 'Digital Object Identifier',
        attr: 'doi',
        text: this.props.doi,
        editTitle: 'Enter a DOI',
        description: 'A DOI is a unique string of characters used to identify digital texts.',
      },
    ];
  }

  tableMultiselectRow(field, options, values_processed) {
    return (<tr key={field.attr} id={`${field.attr}-row`}>
      <td className="section-table-head">
        {field.description ? <HelpPopover
          size="sm"
          name={`help-${field.attr}`}
          key={`help-${field.attr}`}
          placement={'right'}
          title={field.title}
        >
          {field.description}
        </HelpPopover> : '' }
        {field.description ? ' ' : ''}{field.title}
      </td>
      <td id={field.attr}>
        <Select
          isMulti
          key={field.attr}
          name={field.attr}
          value={valuesFromIds(options, values_processed)}
          options={options}
          onChange={(value) => this.handleSelectChange(value, field.attr)}
        />
      </td>
    </tr>);
  }

  tableRow(field) {
    return (<tr key={field.attr} id={`${field.attr}-row`}>
      <td className="section-table-head">
        {field.description ? <HelpPopover
          size="sm"
          name={`help-${field.attr}`}
          key={`help-${field.attr}`}
          placement={'right'}
          title={field.title}
        >
          {field.description}
        </HelpPopover> : '' }
        {field.description ? ' ' : ''}{field.title}
      </td>
      <td id={field.attr}>
        <EditableField
          itemRef={field.attr}
          text={field.text}
          title={field.editTitle}
          limit={40}
          update={(id, attr, value) => this.props.projectUpdate(attr, value)}
          validate={field.validation ? field.validation : () => true}
          warning={field.warning ? field.warning : 'Does not match expectations.'}
          allowEmpty={true}
          textarea={!!field.textarea}
        />
      </td>
    </tr>);
  }

  render() {
    return (<div className="section-list" id='project-metadata-extended'>
      <Collapsible
        trigger={
          <h2 style={{width: '100%'}}>
            Extended Metadata
            <i
              style={{float: 'right'}}
              className="fa fa-angle-down float-end"
            />
          </h2>
        }
        triggerOpenedClassName="section-header"
        triggerClassName="section-header"
      >
        <div className="section-header nested">
          <h3 className="section-head-sub">Additional Descriptive Data</h3>
        </div>
        <Table
          className="section-table"
          responsive
          hover
        >
          <tbody>
            <tr>
              <td className="section-table-head">
                <HelpPopover
                  size="sm"
                  name='help-languages'
                  key='help-languages'
                  placement={'right'}
                  title='Languages'
                >
                  The language in an ePub 3 is set to "English (United States)"
                  by default.
                  Languages set here will override that
                  default. Add languages here in the order they should appear
                  in the ePub's metadata.
                </HelpPopover> Languages
              </td>
              <td id="languages">
                <Select
                  isMulti
                  key="subject-select"
                  name='languages'
                  value={valuesFromIds(LANGUAGES, this.state.languages)}
                  options={LANGUAGES}
                  onChange={(value) => this.handleSelectChange(value, 'languages')}
                />
              </td>
            </tr>
            <tr>
              <td className="section-table-head">Description</td>
              <td id="description">
                <EditableField
                  textarea={true}
                  key="description"
                  itemRef="description"
                  name="description"
                  title="Enter a book description."
                  limit={400}
                  text={this.props.description}
                  update={(id, attr, value) => this.props.projectUpdate(attr, value)}
                  allowEmpty={true}
                />
              </td>
            </tr>
            {this.getOtherFields().map(field => this.tableRow(field))}
          </tbody>
        </Table>
        <div className="section-header nested">
          <h3 className="section-head-sub">Additional Titles</h3>
        </div>
        <Table
          className="section-table"
          responsive
          hover
        >
          <tbody>
            {this.getTitles().map(field => this.tableRow(field))}
          </tbody>
        </Table>
      </Collapsible>
    </div>);
  }
}
